import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import {
  NativeSettings,
  AndroidSettings,
  IOSSettings,
} from "capacitor-native-settings";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

interface PushPermissionContextValue {
  showWarning: boolean;
  requestPermission: () => Promise<void>;
}

const PushPermissionContext = createContext<PushPermissionContextValue | null>(
  null,
);

export const PushPermissionProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { t } = useTranslation();
  const [showWarning, setShowWarning] = useState(false);

  const checkPushPermission = async () => {
    const platform = Capacitor.getPlatform();

    if (platform !== "web") {
      const result = await PushNotifications.checkPermissions();
      // console.log("🔍 [Native] Notification permission:", result.receive);

      if (result.receive === "granted") {
        setShowWarning(false);
      } else {
        setShowWarning(true);
      }
    } else if ("Notification" in window) {
      // On web, force showWarning to false regardless of permission status
      setShowWarning(false);
    }
  };

  useEffect(() => {
    checkPushPermission();

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkPushPermission();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const requestPermission = async () => {
    try {
      const platform = Capacitor.getPlatform();

      if (platform === "ios" || platform === "android") {
        const current = await PushNotifications.checkPermissions();
        // console.log("🔍 Current permission:", current.receive);

        if (current.receive === "prompt") {
          const result = await PushNotifications.requestPermissions();
          // console.log("🔄 Prompted user, new result:", result.receive);

          if (result.receive === "granted") {
            setShowWarning(false);
          } else {
            // denied after prompt → open settings
            try {
              await NativeSettings.open({
                optionAndroid: AndroidSettings.ApplicationDetails,
                optionIOS: IOSSettings.App,
              });
            } catch (error) {
              console.error(" Failed to open settings:", error);
              toast.error(
                t("pushPermission.manualEnable") as string,
              );
            }
          }
        } else if (current.receive === "granted") {
          setShowWarning(false);
        } else if (current.receive === "denied") {
          // denied directly → open settings
          try {
            await NativeSettings.open({
              optionAndroid: AndroidSettings.ApplicationDetails,
              optionIOS: IOSSettings.App,
            });
          } catch (error) {
            console.error(" Failed to open settings:", error);
            toast.error(
              t("pushPermission.genericError") as string,
            );
          }
        }
      }
    } catch (error) {
      console.error(" Failed to request notification permission:", error);
      toast.error(
        t("pushPermission.genericError") as string,
      );
    }
  };

  return (
    <PushPermissionContext.Provider value={{ showWarning, requestPermission }}>
      {children}
    </PushPermissionContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const usePushPermissionWarning = () => {
  const context = useContext(PushPermissionContext);
  if (!context) {
    throw new Error(
      "usePushPermissionWarning must be used within a PushPermissionProvider",
    );
  }
  return context;
};
